import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { API_URL } from "./config";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import LoginOtpPage from "./pages/LoginOtpPage";
import DashboardPage from "./pages/DashboardPage";
import SpacesPage from "./pages/SpacesPage";
import SpaceDetailPage from "./pages/SpaceDetailPage";
import MembersPage from "./pages/MembersPage";
import SettingsPage from "./pages/SettingsPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import PublicDocsPage from "./pages/PublicDocsPage";
import RootEntryPage from "./pages/RootEntryPage";
import ProfilePage from "./pages/ProfilePage";
import InsightsPage from "./pages/InsightsPage";
import EmbedDocsPage from "./pages/EmbedDocsPage";
import EmbedFaqPage from "./pages/EmbedFaqPage";
import FaqsPage from "./pages/FaqsPage";
import FaqSetEditPage from "./pages/FaqSetEditPage";
import HelpCenterPage from "./pages/HelpCenterPage";
import SuperAdminLayout from "./pages/superadmin/SuperAdminLayout";
import SuperAdminOverviewPage from "./pages/superadmin/SuperAdminOverviewPage";
import SuperAdminOrganizationsPage from "./pages/superadmin/SuperAdminOrganizationsPage";
import SuperAdminUsersPage from "./pages/superadmin/SuperAdminUsersPage";
import SuperAdminPlatformPage from "./pages/superadmin/SuperAdminPlatformPage";
import SuperAdminPlatformControlPage from "./pages/superadmin/SuperAdminPlatformControlPage";
import SuperAdminPlansPage from "./pages/superadmin/SuperAdminPlansPage";
import SuperAdminSupportPage from "./pages/superadmin/SuperAdminSupportPage";
import PricingPage from "./pages/PricingPage";

// AI Agent module
import AgentsPage from "./pages/agent/AgentsPage";
import AgentDashboardPage from "./pages/agent/AgentDashboardPage";
import KnowledgeSourcesPage from "./pages/agent/KnowledgeSourcesPage";
import PromptPersonaPage from "./pages/agent/PromptPersonaPage";
import VoiceSelectionPage from "./pages/agent/VoiceSelectionPage";
import ChatLogsPage from "./pages/agent/ChatLogsPage";
import TranscriptDetailPage from "./pages/agent/TranscriptDetailPage";
import KnowledgeGapsPage from "./pages/agent/KnowledgeGapsPage";
import WidgetDefaultPage from "./pages/agent/WidgetDefaultPage";
import WidgetActivePage from "./pages/agent/WidgetActivePage";
import ChatHistoryPage from "./pages/agent/ChatHistoryPage";

// Layout
import DashboardLayout from "./components/layout/DashboardLayout";
import FaviconManager from "./components/FaviconManager";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route Component (redirects authenticated users to dashboard)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user, superAdminViewMode } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    if (user?.platform_role === "super_admin" && superAdminViewMode === "super_admin") {
      return <Navigate to="/dashboard/super-admin/overview" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const SuperAdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.platform_role !== "super_admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const DomainNotConfiguredPage = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-6">
    <div className="max-w-lg text-center space-y-3">
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Domain not configured</h1>
      <p className="text-slate-600 dark:text-slate-400">
        This hostname is not linked to a published DocTree space.
      </p>
      <p className="text-sm text-slate-500 dark:text-slate-500">
        Ask your administrator to verify the domain in Settings → Domain.
      </p>
    </div>
  </div>
);

function AppRoutes({ hostMode = "platform" }) {
  if (hostMode === "blocked") {
    return (
      <Routes>
        <Route path="*" element={<DomainNotConfiguredPage />} />
      </Routes>
    );
  }

  if (hostMode === "custom") {
    return (
      <Routes>
        <Route path="/" element={<RootEntryPage />} />
        <Route path="/docs/:spaceId" element={<PublicDocsPage />} />
        <Route path="/docs/:spaceId/:docId" element={<PublicDocsPage />} />
        <Route path="/embed/faq/:publicId" element={<EmbedFaqPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/verify-email"
        element={
          <PublicRoute>
            <VerifyEmailPage />
          </PublicRoute>
        }
      />
      <Route
        path="/login-otp"
        element={
          <PublicRoute>
            <LoginOtpPage />
          </PublicRoute>
        }
      />
      <Route path="/invite/:token" element={<AcceptInvitePage />} />

      {/* Public Documentation Routes */}
      <Route path="/docs/:spaceId" element={<PublicDocsPage />} />
      <Route path="/docs/:spaceId/:docId" element={<PublicDocsPage />} />
      
      {/* Embeddable FAQ must be before /embed/:slug or "faq" is treated as a doc slug */}
      <Route path="/embed/faq/:publicId" element={<EmbedFaqPage />} />
      <Route path="/embed/:slug" element={<EmbedDocsPage />} />
      <Route path="/embed/:slug/:docId" element={<EmbedDocsPage />} />
      
      <Route path="/pricing" element={<PricingPage />} />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="spaces" element={<SpacesPage />} />
        <Route path="spaces/:spaceId" element={<SpaceDetailPage />} />
        <Route path="members" element={<MembersPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="pricing" element={<PricingPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="insights" element={<InsightsPage />} />
        <Route path="faqs" element={<FaqsPage />} />
        {/* Single route so :setId is "new" for new sets (a dedicated `faqs/new` route leaves useParams().setId undefined). */}
        <Route path="faqs/:setId" element={<FaqSetEditPage />} />
        <Route path="help" element={<HelpCenterPage />} />

        {/* AI Agent module — flat routes; the selected agent lives in useAgents() */}
        <Route path="agent" element={<Navigate to="agent/agents" replace />} />
        <Route path="agent/agents" element={<AgentsPage />} />
        <Route path="agent/dashboard" element={<AgentDashboardPage />} />
        <Route path="agent/knowledge-sources" element={<KnowledgeSourcesPage />} />
        <Route path="agent/prompt-persona" element={<PromptPersonaPage />} />
        <Route path="agent/voice-selection" element={<VoiceSelectionPage />} />
        <Route path="agent/chat-logs" element={<ChatLogsPage />} />
        <Route path="agent/chat-logs/:sessionId" element={<TranscriptDetailPage />} />
        <Route path="agent/knowledge-gaps" element={<KnowledgeGapsPage />} />
        <Route path="agent/widget-default" element={<WidgetDefaultPage />} />
        <Route path="agent/widget-active" element={<WidgetActivePage />} />
        <Route path="agent/chat-history" element={<ChatHistoryPage />} />

        <Route
          path="super-admin"
          element={
            <SuperAdminRoute>
              <SuperAdminLayout />
            </SuperAdminRoute>
          }
        >
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<SuperAdminOverviewPage />} />
          <Route path="organizations" element={<SuperAdminOrganizationsPage />} />
          <Route path="users" element={<SuperAdminUsersPage />} />
          <Route path="plans" element={<SuperAdminPlansPage />} />
          <Route path="pricing" element={<Navigate to="plans" replace />} />
          <Route path="support" element={<SuperAdminSupportPage />} />
          <Route path="platform" element={<SuperAdminPlatformPage />} />
          <Route path="platform-control" element={<SuperAdminPlatformControlPage />} />
        </Route>
      </Route>

      {/* Root: custom docs host → /docs/{spaceId}; else → login */}
      <Route path="/" element={<RootEntryPage />} />
      
      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  const [hostMode, setHostMode] = useState("platform");
  const [checkingHostMode, setCheckingHostMode] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const resolveHostMode = async () => {
      try {
        const host = (window.location.hostname || "").toLowerCase();
        const localHosts = new Set(["localhost", "127.0.0.1", "::1"]);
        if (localHosts.has(host)) {
          if (!cancelled) {
            setHostMode("platform");
            setCheckingHostMode(false);
          }
          return;
        }

        const res = await axios.get(`${API_URL}/tls/allow-domain`, {
          params: { domain: host },
          validateStatus: (s) => s === 200 || s === 403,
        });

        if (cancelled) return;

        if (res.status === 200) {
          const source = res.data?.source;
          if (source === "verified_custom_domain") {
            setHostMode("custom");
          } else if (source === "platform") {
            setHostMode("platform");
          } else {
            setHostMode("blocked");
          }
        } else {
          setHostMode("blocked");
        }
      } catch {
        if (!cancelled) setHostMode("blocked");
      } finally {
        if (!cancelled) setCheckingHostMode(false);
      }
    };

    resolveHostMode();
    return () => {
      cancelled = true;
    };
  }, []);

  if (checkingHostMode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <FaviconManager />
          <AppRoutes hostMode={hostMode} />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
